import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { getMrtColumnId } from "@/components/tables/genericCrudTable.listParams";
import type { EntityAdapter } from "@/components/tables/entityAdapters/entityAdapter";
import { resolveToolbarLayout } from "@/components/tables/toolbar/toolbarLayout";

export interface TableContainerHeightContext {
  hasRowSelection: boolean;
  withSubjectSearch?: boolean;
  readOnlyMode?: boolean;
  hasRowSelectionToolbarElements: boolean; // props.additionalCustomTopToolbarElementsForRowSelection is set
  entityAdapter?: EntityAdapter;
}

export const getTableContainerMaxHeight = (
  ctx: TableContainerHeightContext
): string => {
  const baseHeight = "clamp(350px, calc(100% - ";
  const { rowSelectionElementsPosition } = resolveToolbarLayout(
    ctx.entityAdapter
  );
  const baseTopToolbarHeight =
    rowSelectionElementsPosition === "ownRow" && ctx.hasRowSelection
      ? "17rem"
      : ctx.withSubjectSearch
      ? "17rem"
      : ctx.readOnlyMode
      ? "8rem" 
      : "10.5rem";

  const additionalToolbarHeight =
    rowSelectionElementsPosition === "besideCustomAction" &&
    ctx.hasRowSelectionToolbarElements
      ? ctx.hasRowSelection
        ? "8rem"
        : "4rem"
      : "0rem"; 

  return `${baseHeight}(${baseTopToolbarHeight} + ${additionalToolbarHeight})), 9999px)`;
};

export const getSearchFieldPlaceholder = (
  t: (key: string) => string,
  entityAdapter?: EntityAdapter
): string => entityAdapter?.searchPlaceholder?.(t) ?? t("search_placeholder");

export const getSearchFieldAutoFocus = (entityAdapter?: EntityAdapter): boolean =>
  entityAdapter?.searchAutoFocus ?? true;

export const getRowBackgroundColor = (
  row: { index: number; original: Record<string, any> },
  entityAdapter?: EntityAdapter
): string => {
  const entityColor = entityAdapter?.rowBackgroundColor?.(row);
  if (entityColor) {
    return entityColor;
  }
  return row.index % 2 === 0 ? "#f5f5f5" : "#FFF";
};

export const getBodyCellSx = (
  rowData: Record<string, any>,
  baseSx: Record<string, unknown>,
  entityAdapter?: EntityAdapter
): Record<string, unknown> =>
  entityAdapter?.bodyCellSx?.(rowData, baseSx) || baseSx;

export const getTableCellWhiteSpace = (entityAdapter?: EntityAdapter): string =>
  entityAdapter?.cellWhiteSpace ?? "";

export const getColumnVisibilityHiddenByDefault = (
  columns: CrudColumnDef[]
): Record<string, boolean> => {
  const columnVisibility: Record<string, boolean> = {};
  for (const column of columns) {
    const columnId = getMrtColumnId(column);
    if (column.hiddenByDefault && columnId) {
      columnVisibility[columnId] = false;
    }
  }
  return columnVisibility;
};
